/**
 * 首尾帧对比的纯逻辑（Wiki F-01 ~ F-07）。
 *
 * 为什么不在组件里算：叠加与像素差异都要在 canvas 上做，
 * 把「取哪两张图」「差异阈值」「统计」抽出来才能进单测。
 */

export interface FrameCandidate {
  /** 首帧 / 尾帧 / 中间关键帧 */
  role: "keyframe" | "endFrame" | "midFrame";
  label: string;
  url: string | null;
}

/** 该镜头实际有哪几帧（顺序：首帧 → 中间 → 尾帧；缺的不返回）。 */
export function frameCandidates(shot: {
  keyframe?: { url: string | null } | null;
  endFrame?: { url: string | null } | null;
}): FrameCandidate[] {
  const out: FrameCandidate[] = [];
  if (shot.keyframe?.url) out.push({ role: "keyframe", label: "首帧", url: shot.keyframe.url });
  if (shot.endFrame?.url) out.push({ role: "endFrame", label: "尾帧", url: shot.endFrame.url });
  return out;
}

/**
 * 叠加模式的 canvas 混合运算符。
 * 用 `lighter`（相加）+ 50% 不透明度 ≈ 各占一半，比 `source-over` 好看；
 * 首帧和尾帧亮度差大时相加会过曝，所以调用方应把 alpha 降到 0.5。
 */
export const OVERLAY_ALPHA = 0.5;

/** 像素差异阈值：0–255 的 RGB 距离，超过才算「变了」。低于 24 基本是压缩噪声。 */
export const DIFF_THRESHOLD = 24;

export interface DiffStats {
  /** 变化像素数 */
  changed: number;
  /** 总像素数 */
  total: number;
  /** 变化占比 0–1 */
  ratio: number;
}

/** 两帧像素比较，返回变化统计；`pixels` 是同一个 RGBA 缓冲区两次读出的结果。 */
export function diffFrames(a: ImageData, b: ImageData, threshold = DIFF_THRESHOLD): DiffStats {
  const n = Math.min(a.data.length, b.data.length);
  let changed = 0;
  for (let i = 0; i < n; i += 4) {
    const dr = (a.data[i] ?? 0) - (b.data[i] ?? 0);
    const dg = (a.data[i + 1] ?? 0) - (b.data[i + 1] ?? 0);
    const db = (a.data[i + 2] ?? 0) - (b.data[i + 2] ?? 0);
    if (Math.sqrt(dr * dr + dg * dg + db * db) > threshold) changed += 1;
  }
  const total = Math.floor(n / 4);
  return { changed, total, ratio: total === 0 ? 0 : changed / total };
}

/**
 * 差异图的染色：变化的像素涂琥珀，没变的压暗。
 * 直接改传入的 ImageData（调用方随后 putImageData）。
 */
export function paintDiff(source: ImageData, other: ImageData, threshold = DIFF_THRESHOLD): ImageData {
  const n = Math.min(source.data.length, other.data.length);
  for (let i = 0; i < n; i += 4) {
    const dr = (source.data[i] ?? 0) - (other.data[i] ?? 0);
    const dg = (source.data[i + 1] ?? 0) - (other.data[i + 1] ?? 0);
    const db = (source.data[i + 2] ?? 0) - (other.data[i + 2] ?? 0);
    const changed = Math.sqrt(dr * dr + dg * dg + db * db) > threshold;
    if (changed) {
      source.data[i] = 224;
      source.data[i + 1] = 164;
      source.data[i + 2] = 90;
      source.data[i + 3] = 255;
    } else {
      const grey = Math.round(((source.data[i] ?? 0) + (source.data[i + 1] ?? 0) + (source.data[i + 2] ?? 0)) / 3);
      source.data[i] = grey * 0.35;
      source.data[i + 1] = grey * 0.35;
      source.data[i + 2] = grey * 0.35;
      source.data[i + 3] = 255;
    }
  }
  return source;
}

/** 台词字数（审核区要显示「台词 X 字」，用于判断语速是否合理）。 */
export function dialogueChars(dialogue: Array<{ text: string }>): number {
  return dialogue.reduce((sum, line) => sum + [...line.text.replace(/\s/g, "")].length, 0);
}

/** 建议片长：按字数估（每秒 5 字），夹在 0.5–15 秒之间。 */
export function suggestedDuration(chars: number): number {
  if (chars === 0) return 0.5;
  return Math.min(15, Math.max(0.5, Math.round((chars / 5) * 10) / 10));
}
